import { definePreset } from "@primeuix/themes";
import Aura from "@primeuix/themes/aura";

export const EbookPreset = definePreset(Aura, {
        extend: {
                ebook: {
                        surface: {
                                workspace: "#e1e8f2",
                                appBackground: "#cfd9e2",
                        },
                        status: {
                                warning: "#b45309",
                                danger: "#be123c",
                        },
                        shadow: {
                                panel: "0 18px 48px rgb(23 50 77 / 12%)",
                        },
                        note: {
                                yellow: { color: "#E7B928", hoverColor: "#D2A515", pressedColor: "#BB900E", highlightColor: "#FFF4C7" },
                                green: { color: "#55B992", hoverColor: "#3FA47D", pressedColor: "#318F6B", highlightColor: "#E8F7F1" },
                                blue: { color: "#5F96E8", hoverColor: "#437FDE", pressedColor: "#2F6CCB", highlightColor: "#EAF2FF" },
                                purple: { color: "#9277D8", hoverColor: "#7B5FCA", pressedColor: "#694CB9", highlightColor: "#F1EDFC" },
                                orange: { color: "#E9945C", hoverColor: "#D97E3E", pressedColor: "#C96A28", highlightColor: "#FDEDE2" },
                        },
                        button: {
                                secondary: {
                                        hoverBorder: "#8cb9de",
                                        hoverBackground: "#f3f7fa",
                                        pressedBackground: "#e8f2fa",
                                        pressedColor: "#194f7f",
                                },
                                disabled: {
                                        primaryBackground: "#d8dee5",
                                        primaryColor: "#8d9ba7",
                                        secondaryBackground: "#f3f5f7",
                                        dangerBackground: "#e8caca",
                                        dangerColor: "#9e6f6f",
                                },
                                text: {
                                        hoverBackground: "#f3f7fa",
                                        pressedBackground: "#e8f2fa",
                                        color: "#17324d",
                                        pressedColor: "#17324d",
                                },
                        },
                },
        },
        components: {
                button: {
                        root: {
                                borderRadius: "10px",
                                paddingX: "1.5rem",
                                paddingY: "0.75rem",
                                transitionDuration: "0s",
                                label: {
                                        fontWeight: "inherit",
                                },
                        },
                        colorScheme: {
                                light: {
                                        root: {
                                                danger: {
                                                        background: "#b8462c",
                                                        hoverBackground: "#aa3232",
                                                        activeBackground: "#8f2929",
                                                        borderColor: "#b8462c",
                                                        hoverBorderColor: "#aa3232",
                                                        activeBorderColor: "#8f2929",
                                                        color: "#ffffff",
                                                        hoverColor: "#ffffff",
                                                        activeColor: "#ffffff",
                                                        focusRing: {
                                                                color: "#e9a4a4",
                                                                shadow: "none",
                                                        },
                                                },
                                        },
                                        outlined: {
                                                primary: {
                                                        hoverBackground: "{ebook.button.secondary.hover.background}",
                                                        activeBackground: "{ebook.button.secondary.pressed.background}",
                                                        borderColor: "{primary.color}",
                                                        color: "{primary.color}",
                                                },
                                                secondary: {
                                                        hoverBackground: "{ebook.button.secondary.hover.background}",
                                                        activeBackground: "{ebook.button.secondary.pressed.background}",
                                                        borderColor: "{surface.300}",
                                                        color: "{ebook.button.text.color}",
                                                },
                                        },
                                        text: {
                                                primary: {
                                                        hoverBackground: "transparent",
                                                        activeBackground: "transparent",
                                                        color: "#2c75b8",
                                                },
                                                info: {
                                                        hoverBackground: "transparent",
                                                        activeBackground: "transparent",
                                                        color: "#2c75b8",
                                                },
                                                secondary: {
                                                        hoverBackground: "{ebook.button.text.hover.background}",
                                                        activeBackground: "{ebook.button.text.pressed.background}",
                                                        color: "{ebook.button.text.color}",
                                                },
                                        },
                                },
                        },
                        css: () => `
                                .p-button {
                                        box-sizing: border-box;
                                        font-weight: 500;
                                        transform: none;
                                        transition: none;
                                }

                                a.p-button {
                                        margin: 0;
                                        line-height: inherit;
                                        letter-spacing: inherit;
                                        text-decoration: none;
                                }

                                a.p-button:hover {
                                        text-decoration: none;
                                }

                                .p-button.p-button-text {
                                        font-family: var(--font-ss3-16-lh100pct-family);
                                        font-size: var(--font-ss3-16-lh100pct-size);
                                        font-weight: 500;
                                        font-style: normal;
                                        line-height: var(--font-ss3-16-lh100pct-line-height);
                                        letter-spacing: 0;
                                        text-align: center;
                                        text-decoration: none;
                                        padding-top: 3px;
                                        padding-bottom: 3px;
                                }

                                .p-button:hover,
                                .p-button:active,
                                .p-button:focus-visible {
                                        transform: none;
                                        transition: none;
                                }

                                .p-button-text.p-button-secondary,
                                .p-button-text.p-button-secondary:hover,
                                .p-button-text.p-button-secondary:focus-visible {
                                        border: 0;
                                }

                                .p-button-text.p-button-secondary:not(:disabled):active {
                                        border: 0;
                                        color: var(--p-ebook-button-text-pressed-color);
                                }

                                .p-button-text.p-button-secondary:not(:disabled):active:hover {
                                        color: #256fae;
                                        background: #e8f2fa;
                                }

                                .p-button .p-ink {
                                        display: none;
                                }

                                .p-button:not(.p-button-outlined):not(.p-button-text):not(.p-button-link):not(.p-button-secondary):not(.p-button-success):not(.p-button-info):not(.p-button-warn):not(.p-button-help):not(.p-button-danger):not(.p-button-contrast) {
                                        font-weight: 700;
                                }

                                .p-button:not(.p-button-outlined):not(.p-button-text):not(.p-button-link):not(.p-button-secondary):not(.p-button-success):not(.p-button-info):not(.p-button-warn):not(.p-button-help):not(.p-button-danger):not(.p-button-contrast):disabled {
                                        border-color: var(--p-ebook-button-disabled-primary-background);
                                        background: var(--p-ebook-button-disabled-primary-background);
                                        color: var(--p-ebook-button-disabled-primary-color);
                                        opacity: 1;
                                }

                                .p-button-danger:disabled {
                                        border-color: var(--p-ebook-button-disabled-danger-background);
                                        background: var(--p-ebook-button-disabled-danger-background);
                                        color: var(--p-ebook-button-disabled-danger-color);
                                        opacity: 1;
                                }

                                .p-button-outlined:not(:disabled):hover {
                                        border-color: var(--p-ebook-button-secondary-hover-border);
                                }

                                .p-button-outlined:not(:disabled):active {
                                        border-color: var(--p-primary-color);
                                        color: var(--p-primary-active-color);
                                }

                                .p-button.p-button-outlined.p-button-secondary:not(:disabled):active {
                                        color: var(--p-ebook-button-secondary-pressed-color);
                                }

                                .p-button-outlined:disabled {
                                        border-color: var(--p-content-border-color);
                                        background: var(--p-ebook-button-disabled-secondary-background);
                                        color: var(--p-surface-500);
                                        opacity: 1;
                                }

                                .p-button-text:not(.p-button-secondary):not(:disabled):hover .p-button-label {
                                        text-decoration: underline;
                                        text-decoration-thickness: 1px;
                                        text-underline-offset: 0.12em;
                                }

                                .p-button-text:not(.p-button-secondary):not(:disabled):active {
                                        color: var(--p-ebook-button-text-pressed-color);
                                }

                                .p-button-text:not(.p-button-secondary):not(:disabled):active .p-button-label,
                                .p-button-text:not(.p-button-secondary):focus-visible .p-button-label {
                                        text-decoration: none;
                                }

                                .p-button-text:disabled {
                                        color: var(--p-surface-500);
                                        opacity: 1;
                                }
                        `,
                },
        },
        semantic: {
                primary: {
                        50: "#eaf3fb",
                        100: "#e8f2fb",
                        200: "#d5e0e9",
                        300: "#b8c7d4",
                        400: "#5ea3dd",
                        500: "#256fae",
                        600: "#1f5f96",
                        700: "#194f7e",
                        800: "#17324d",
                        900: "#17324d",
                        950: "#17324d",
                },
                focusRing: {
                        width: "2px",
                        color: "#5ea3dd",
                        offset: "2px",
                },
                colorScheme: {
                        light: {
                                surface: {
                                        0: "#ffffff",
                                        50: "#f3f7fa",
                                        100: "#eaf3fb",
                                        200: "#e8f2fb",
                                        300: "#d5e0e9",
                                        400: "#b8c7d4",
                                        500: "#a7b3be",
                                        600: "#7a8b9a",
                                        700: "#53677a",
                                        800: "#17324d",
                                        900: "#17324d",
                                        950: "#17324d",
                                },
                                primary: {
                                        color: "{primary.500}",
                                        contrastColor: "#ffffff",
                                        hoverColor: "{primary.600}",
                                        activeColor: "{primary.700}",
                                },
                                highlight: {
                                        background: "#e8f2fb",
                                        focusBackground: "#eaf3fb",
                                        color: "#17324d",
                                        focusColor: "#17324d",
                                },
                                formField: {
                                        background: "#ffffff",
                                        disabledBackground: "#f3f7fa",
                                        borderColor: "#cad4de",
                                        hoverBorderColor: "#8cb9de",
                                        focusBorderColor: "#256fae",
                                        color: "#17324d",
                                        disabledColor: "#a7b3be",
                                        placeholderColor: "#7a8b9a",
                                },
                                text: {
                                        color: "#17324d",
                                        hoverColor: "#17324d",
                                        mutedColor: "#53677a",
                                        hoverMutedColor: "#17324d",
                                },
                                content: {
                                        background: "#ffffff",
                                        hoverBackground: "#f3f7fa",
                                        borderColor: "#d5e0e9",
                                        color: "#17324d",
                                        hoverColor: "#17324d",
                                },
                        },
                },
        },
});
